/* eslint-disable */ // Prop types são definidos no final
import React, { useMemo, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import Tippy from "@tippyjs/react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatNumber } from "utils/formatNumberUtils";
import "utils/chartjs-setup";
import { getProgressColor } from "utils/thresholdUtils";
import AuthContext from "contexts/AuthProvider";

// --- Funções Utilitárias para Cores ---
function generateColorPalette(baseColor) {
  // Validação da cor base
  if (!baseColor || !/^#[0-9A-F]{6}$/i.test(baseColor)) {
    baseColor = "#4A90E2"; // Fallback para azul padrão
  }

  // Remove o # e converte para RGB
  const hex = baseColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Gera variações da cor base
  const primary = baseColor;
  const primaryLight = `rgba(${r}, ${g}, ${b}, 0.1)`;
  const primaryMedium = `rgba(${r}, ${g}, ${b}, 0.6)`;
  const primaryDark = `rgba(${r}, ${g}, ${b}, 0.8)`;
  
  // Cor complementar para contraste (versão suavizada)
  const complementary = `hsl(${(r * 0.299 + g * 0.587 + b * 0.114) > 128 ? 0 : 180}, 50%, 70%)`;
  
  // Cores neutras modernas
  const neutral = {
    50: "#FAFAFA",
    100: "#F5F5F5", 
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121"
  };

  return {
    primary,
    primaryLight,
    primaryMedium,
    primaryDark,
    complementary,
    neutral
  };
}

function pastelColor(baseColor) {
  switch (baseColor) {
    case "#4caf50":
      return "#C8E6C9";
    case "#ffeb3b":
      return "#FFF9C4";
    case "#f44336":
      return "#FFCDD2";
    default:
      return "#fff";
  }
}

// --- Estilos Modernizados ---
const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
    alignItems: "stretch",
  },
  chartSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: "1px 0px 6px 10px",
    position: "relative",
    minHeight: "200px",
  },
  chartArea: {
    flex: 1,
    position: "relative",
    width: "100%",
    height: "100%",
  },
  progressSection: {
    width: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginRight: 10,
    marginLeft: 2,
    flexShrink: 0,
  },
  tableContainer: {
    margin: "16px",
    marginBottom: "40px",
    overflowX: "auto",
    flex: 1,
    alignContent: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
  },
  kpiTable: {
    borderCollapse: "collapse",
    width: "100%",
    fontSize: "0.875rem",
    lineHeight: "1.4rem",
    color: "#374151",
    textAlign: "center",
    borderRadius: "8px",
    overflow: "hidden",
  },
  kpiTableHeaderCell: {
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
    backgroundColor: "#F9FAFB",
    fontWeight: "600",
    color: "#374151",
    fontSize: "0.875rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  kpiTableCell: {
    padding: "12px 16px",
    borderBottom: "1px solid #F3F4F6",
    transition: "background-color 0.15s ease",
  },
  rowHeader: {
    textAlign: "left",
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    color: "#374151",
  },
  noDataMessage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "200px",
    color: "#6B7280",
    fontSize: "0.875rem",
    fontStyle: "italic",
  },

}));

// --- Componente Principal Modernizado ---
function MixedBarLineChart({
  timeAgg,
  viewMode,
  data,
  trackingMode,
  thresholdsConfig,
  unit = "",
  showUnitBefore = false,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  
  // Obtém as cores da implementação do usuário
  const clientColor = userDetails?.implementation_color || "#4A90E2";
  const clientColor2 = userDetails?.implementation_color2;
  const clientColor3 = userDetails?.implementation_color3;
  
  const colorPalette = useMemo(() => generateColorPalette(clientColor), [clientColor]);

  // --- Processamento dos Dados com Cores Dinâmicas ---
  const { chartData, chartOptions } = useMemo(() => {
    const source = data;
    if (
      !source ||
      !source.labels ||
      !source.series ||
      source.series.length < 2
    ) {
      return { chartData: { labels: [], datasets: [] }, chartOptions: {} };
    }

    // Encontrar o índice da série "Real" pelo nome
    const seriesNames = source.seriesNames || [];
    const realIndex = seriesNames.findIndex(name => name === "Real");
    
    // Se não encontrou "Real", assumir que é a última série (compatibilidade com dados antigos)
    const realIdx = realIndex !== -1 ? realIndex : 1;

    // Mapear cores específicas para cada série com fallback
    const seriesColors = {
      "Meta Inicial": clientColor, // color (baseline)
      "Meta": clientColor2 || colorPalette.primaryDark, // color2 ou fallback
      "Real": clientColor3 || colorPalette.complementary, // color3 ou fallback
    };

    // Criar datasets dinamicamente
    const datasets = [];
    
    // Primeiro, adicionar todas as séries que não são "Real" como linhas
    seriesNames.forEach((name, index) => {
      if (index !== realIdx) {
        // Usar cor específica ou fallback para cor primária
        const lineColor = seriesColors[name] || colorPalette.primary;
        
        datasets.push({
          type: "line",
          label: name,
          data: source.series[index],
          borderColor: lineColor,
          backgroundColor: `${lineColor}20`, // 20 = transparência em hex
          borderWidth: 3,
          pointBackgroundColor: lineColor,
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.2,
          yAxisID: "y",
          fill: false,
        });
      }
    });

    // Depois, adicionar a série "Real" como barra
    const realColor = seriesColors["Real"];
    datasets.push({
      type: "bar",
      label: seriesNames[realIdx] || "Real",
      data: source.series[realIdx],
      backgroundColor: realColor,
      borderColor: realColor,
      borderWidth: 0,
      barPercentage: 0.7,
      categoryPercentage: 0.85,
      yAxisID: "y",
      borderRadius: 4,
    });

    const chartJsData = {
      labels: source.labels,
      datasets: datasets,
    };

    // --- Opções do Gráfico Modernizadas ---
    const options = {
      responsive: true,
      maintainAspectRatio: false,
             animation: {
         duration: 1000,
         easing: "easeOutQuart",
       },
      plugins: {
        legend: {
          position: "bottom",
          align: "center",
          labels: {
            usePointStyle: true,
            boxWidth: 10,
            padding: 20,
            font: {
              size: 12,
              weight: "500",
            },
            color: colorPalette.neutral[700],
          },
        },
        tooltip: {
          enabled: true,
          mode: "index",
          intersect: false,
          backgroundColor: "#FFFFFF",
          titleColor: colorPalette.neutral[900],
          bodyColor: colorPalette.neutral[700],
          borderColor: colorPalette.neutral[200],
          borderWidth: 1,
          padding: 16,
          cornerRadius: 8,
          usePointStyle: true,
          titleFont: {
            size: 13,
            weight: "600",
          },
          bodyFont: {
            size: 12,
          },
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += formatNumber(context.parsed.y, unit, showUnitBefore);
              }
              return label;
            },
            afterBody: function (context) {
              // Encontrar items de Meta e Real pelo label do dataset
              const metaItem = context.find((item) => item.dataset.label === "Meta");
              const realItem = context.find((item) => item.dataset.label === "Real");

              if (
                metaItem &&
                realItem &&
                metaItem.parsed.y > 0 &&
                realItem.parsed.y !== null
              ) {
                const metaVal = metaItem.parsed.y;
                const realVal = realItem.parsed.y;
                const ratio = ((realVal / metaVal) * 100).toFixed(1);
                return `\n${t("whatshappening.tooltip.real_meta_ratio")} ${ratio}%`;
              }
              return "";
            },
          },
        },
        title: {
          display: false,
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: false,
          },
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 11,
              weight: "500",
            },
            color: colorPalette.neutral[600],
            padding: 8,
          },
        },
        y: {
          display: true,
          position: "left",
          title: {
            display: false,
          },
          grid: {
            display: true,
            color: colorPalette.neutral[200],
            lineWidth: 1,
            drawBorder: false,
          },
          ticks: {
            font: {
              size: 11,
              weight: "500",
            },
            color: colorPalette.neutral[600],
            padding: 8,
            callback: function(value) {
              return formatNumber(value, unit, showUnitBefore);
            }
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
      elements: {
        point: {
          hoverRadius: 8,
          hoverBorderWidth: 3,
        },
      },
    };

    return { chartData: chartJsData, chartOptions: options };
  }, [data, t, colorPalette, unit, showUnitBefore, clientColor, clientColor2, clientColor3]);

  // --- Cálculo de Atingimento ---
  const { atingimento, progressColor, metaVal, realVal } = useMemo(() => {
    const seriesNames = data?.seriesNames || [];
    const series = data?.series || [];
    
    // Encontrar índices de "Meta" e "Real"
    const metaIndex = seriesNames.findIndex(name => name === "Meta");
    const realIndex = seriesNames.findIndex(name => name === "Real");
    
    // Usar índices padrão se não encontrar (compatibilidade com dados antigos)
    const metaIdx = metaIndex !== -1 ? metaIndex : 0;
    const realIdx = realIndex !== -1 ? realIndex : 1;
    
    const realArr = series[realIdx] || [];
    const metaArr = series[metaIdx] || [];
    let ratio = 0;
    let rVal = 0;
    let mVal = 0;
    let foundIndex = -1;

    for (let i = Math.min(metaArr.length, realArr.length) - 1; i >= 0; i--) {
      const mv = metaArr[i];
      const rv = realArr[i];
      if (
        mv !== null &&
        mv !== undefined &&
        mv > 0 &&
        rv !== null &&
        rv !== undefined
      ) {
        foundIndex = i;
        break;
      }
    }

    if (foundIndex !== -1) {
      rVal = realArr[foundIndex];
      mVal = metaArr[foundIndex];
      if (mVal > 0) {
        ratio = rVal / mVal;
      }
    }

    const ating = Math.round(ratio * 100);
    const pColor = getProgressColor(ratio, trackingMode, thresholdsConfig);

    return {
      atingimento: ating,
      progressColor: pColor,
      metaVal: mVal,
      realVal: rVal,
    };
  }, [data, trackingMode, thresholdsConfig]);

  // --- Renderização do Gráfico ---
  function renderChart() {
    if (
      !chartData ||
      !chartData.datasets ||
      chartData.datasets.length === 0 ||
      chartData.labels.length === 0
    ) {
      return (
        <div className={classes.noDataMessage}>
          {t("whatshappening.messages.no_data")}
        </div>
      );
    }
    
    return (
      <div className={classes.chartSection}>
        <div className={classes.chartArea}>
          <Chart
            key={`${timeAgg}-${viewMode}`}
            type="bar"
            data={chartData}
            options={chartOptions}
          />
        </div>
      </div>
    );
  }

  // --- Renderização da Tabela Modernizada ---
  function renderTable() {
    const labels = data?.labels || [];
    const series = data?.series || [];
    const seriesNames = data?.seriesNames || [];

    if (!labels.length || series.length < 2) {
      return (
        <div className={classes.noDataMessage}>
          {t("whatshappening.messages.no_data")}
        </div>
      );
    }

    // Encontrar índices de "Meta" e "Real"
    const metaIndex = seriesNames.findIndex(name => name === "Meta");
    const realIndex = seriesNames.findIndex(name => name === "Real");
    
    // Usar índices padrão se não encontrar (compatibilidade com dados antigos)
    const metaIdx = metaIndex !== -1 ? metaIndex : 0;
    const realIdx = realIndex !== -1 ? realIndex : 1;

    const metaValues = series[metaIdx] || [];
    const realValues = series[realIdx] || [];
    const metaName = seriesNames[metaIdx] || t("whatshappening.labels.meta");
    const realName = seriesNames[realIdx] || t("whatshappening.labels.real");

    return (
      <div className={classes.tableContainer}>
        <table className={classes.kpiTable}>
          <thead>
            <tr>
              <th className={classes.kpiTableHeaderCell} />
              {labels.map((lbl, idx) => (
                <th key={idx} className={classes.kpiTableHeaderCell}>
                  {lbl}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {metaName}
              </td>
              {labels.map((_, i) => (
                <td key={i} className={classes.kpiTableCell}>
                  {metaValues[i] != null
                    ? formatNumber(metaValues[i], unit, showUnitBefore)
                    : "--"}
                </td>
              ))}
            </tr>
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {realName}
              </td>
              {labels.map((_, i) => (
                <td key={i} className={classes.kpiTableCell}>
                  {realValues[i] != null
                    ? formatNumber(realValues[i], unit, showUnitBefore)
                    : "--"}
                </td>
              ))}
            </tr>
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {t("whatshappening.labels.comparison_goal_percent")}
              </td>
              {labels.map((_, i) => {
                const mv = metaValues[i];
                const rv = realValues[i];
                let comparisonRatio = null;

                if (mv != null && mv > 0 && rv != null) {
                  comparisonRatio = rv / mv;
                }

                if (comparisonRatio !== null) {
                  const ratioPercent = (comparisonRatio * 100).toFixed(1);
                  const baseColor = getProgressColor(
                    comparisonRatio,
                    trackingMode,
                    thresholdsConfig
                  );
                  const bgColor = pastelColor(baseColor);
                  return (
                    <td
                      key={i}
                      style={{ 
                        backgroundColor: bgColor,
                        fontWeight: "600",
                        color: "#374151"
                      }}
                      className={classes.kpiTableCell}
                    >
                      {ratioPercent}%
                    </td>
                  );
                }
                return (
                  <td key={i} className={classes.kpiTableCell}>
                    --
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // --- Renderização Principal ---
  return (
    <div className={classes.container}>
      <div className={classes.contentWrapper}>
        {viewMode === "chart" ? renderChart() : renderTable()}

        {/* Gauge Circular Modernizado */}
        <div className={classes.progressSection}>
          <Tippy
            content={`
              ${t("whatshappening.labels.meta")}: 
              ${formatNumber(metaVal, unit, showUnitBefore)} 
              | 
              ${t("whatshappening.labels.real")}: 
              ${formatNumber(realVal, unit, showUnitBefore)}
            `}
            arrow
            theme="light"
            placement="left"
            animation="scale"
            duration={200}
          >
                         <div style={{ 
               width: "100%", 
               maxWidth: "50px", 
               height: "auto"
             }}>
              <CircularProgressbar
                value={atingimento}
                text={`${atingimento}%`}
                styles={buildStyles({
                  pathColor: progressColor,
                  textSize: "24px",
                  textColor: colorPalette.neutral[800],
                  trailColor: colorPalette.neutral[200],
                  strokeLinecap: "round",
                })}
              />
            </div>
          </Tippy>
        </div>
      </div>
    </div>
  );
}

// --- PropTypes ---
MixedBarLineChart.propTypes = {
  timeAgg: PropTypes.string.isRequired,
  viewMode: PropTypes.string.isRequired,
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    seriesNames: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  trackingMode: PropTypes.string.isRequired,
  thresholdsConfig: PropTypes.object.isRequired,
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
};

export default React.memo(MixedBarLineChart);
